import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BookingModel,
  SetupIntentResult,
  UserBillingAddressModel,
  UserModel,
  UserPreferencesModel,
} from '@app/core/models';
import { from, Observable, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FirebaseAuthenticationService } from '../firebase';

/** Payload for `POST /users/card-on-file` — saves a card without charging it. */
export interface SaveCardOnFilePayload {
  paymentMethodId: string;
  cardholderName: string;
  bookingId: string;
  consent: { textVersion: string };
}

/** One Stripe PaymentMethod as returned by `GET /users/stripe/customer/cards`. */
export interface StripeCustomerCard {
  id: string;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
}

export interface StripeCustomerCardsResult {
  data: StripeCustomerCard[];
}

/**
 * One saved card as returned by our canonical `GET /users/card-on-file`
 * list (no PAN, no consent metadata) — unlike `StripeCustomerCard`, this
 * reflects only the cards the guest actually saved on file, and flags
 * which one is the default.
 */
export interface SavedCard {
  paymentMethodId: string;
  cardholderName: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export interface SavedCardsResult {
  cards: SavedCard[];
  defaultPaymentMethodId: string | null;
}

@Injectable()
export class UserService {
  constructor(
    private _http: HttpClient,
    private authService: FirebaseAuthenticationService,
  ) {}

  upsertStripeCustomer(data: UserBillingAddressModel): Observable<UserModel> {
    return this._http.put<UserModel>(
      `${environment.api.serverUrl}/users/stripe/customer`,
      { address: data },
    );
  }

  getStripeCustomerBillingAddress(): Observable<UserBillingAddressModel> {
    return this._http.get<UserBillingAddressModel>(
      `${environment.api.serverUrl}/users/stripe/customer/billing-address`,
    );
  }

  updateUserPreferences(
    preferences: UserPreferencesModel,
    uid: string,
  ): Observable<UserModel> {
    return this._http.put<UserModel>(
      `${environment.api.serverUrl}/users/preferences/${uid}`,
      preferences,
    );
  }

  createSetupIntent(): Observable<SetupIntentResult> {
    return this._http.post<SetupIntentResult>(
      `${environment.api.serverUrl}/users/create-setup-intent`,
      {},
    );
  }

  /**
   * Saves a card for future incidental charges — does NOT charge anything.
   * The backend responds 422 (message key
   * `card-holder-does-not-match-booking-guests`) when `cardholderName` doesn't
   * match any guest on the booking.
   */
  saveCardOnFile(payload: SaveCardOnFilePayload): Observable<unknown> {
    return this._http.post(
      `${environment.api.serverUrl}/users/card-on-file`,
      payload,
    );
  }

  /** Cards attached to the guest's Stripe customer (brand/last4/exp only). */
  getStripeCustomerCards(): Observable<StripeCustomerCardsResult> {
    return this._http.get<StripeCustomerCardsResult>(
      `${environment.api.serverUrl}/users/stripe/customer/cards`,
    );
  }

  /**
   * The guest's saved cards — our canonical list (no PAN, no consent),
   * including which one is the default.
   */
  listSavedCards(): Observable<SavedCardsResult> {
    return this._http.get<SavedCardsResult>(
      `${environment.api.serverUrl}/users/card-on-file`,
    );
  }

  /**
   * Revokes one saved card (detach in Stripe) and marks it revoked.
   * Idempotent — safe to call when the card is already gone.
   */
  deleteCardOnFile(paymentMethodId: string): Observable<unknown> {
    return this._http.delete(
      `${environment.api.serverUrl}/users/card-on-file`,
      { params: { paymentMethodId } },
    );
  }

  sendBookingInvitations(
    bookingId: string,
    emails: string[],
  ): Observable<BookingModel> {
    return this._http.post<BookingModel>(
      `${environment.api.serverUrl}/users/booking/${bookingId}/invite-guests`,
      { emails },
    );
  }

  syncUserBooking(locationId: string, bookingId: string): Observable<void> {
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        return this._http.post<void>(
          `${environment.api.serverUrl}/users/sync-booking`,
          { locationId, bookingId },
          { headers: { Authorization: `Bearer ${data.token}` } },
        );
      }),
    );
  }

  claimWelcomeGiftCoupon(): Observable<void> {
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        return this._http.post<void>(
          `${environment.api.serverUrl}/users/welcome-gift-coupon`,
          { source: 'home-welcome-gift' },
          { headers: { Authorization: `Bearer ${data.token}` } },
        );
      }),
    );
  }
}
